<template>
  <div class="modern-home">
    <!-- ✨ Hero Slider with Responsive Images -->
    <div v-if="banners.length > 0" id="heroCarousel" class="carousel slide carousel-fade" data-bs-ride="carousel" data-bs-interval="5000">
      <div class="carousel-indicators">
        <button
          v-for="(banner, index) in banners"
          :key="'indicator-' + banner._id"
          type="button"
          data-bs-target="#heroCarousel"
          :data-bs-slide-to="index"
          :class="{ active: index === 0 }"
        ></button>
      </div>
      
      <div class="carousel-inner">
        <div
          v-for="(banner, index) in banners"
          :key="banner._id"
          :class="['carousel-item', index === 0 ? 'active' : '']"
          class="hero-slide-modern"
        >
          <!-- RESPONSIVE IMAGE with dynamic resizing -->
          <img 
            :src="banner.bannerImageUrl"
            :srcset="generateSrcset(banner.bannerImageUrl)"
            sizes="100vw"
            :alt="banner.title || 'Banner'"
            class="hero-slide-img"
            loading="eager"
            fetchpriority="high"
          >
          
          <div class="hero-overlay-modern">
            <div class="hero-content animate-fade-in">
              <h1 v-if="banner.title" class="hero-title">{{ banner.title }}</h1>
              <p v-if="banner.description" class="hero-description">{{ banner.description }}</p>
              <a
                v-if="banner.redirectLink"
                :href="banner.redirectLink"
                class="btn-hero-cta"
              >
                {{ banner.buttonText || 'Explore Now' }}
                <i class="fas fa-arrow-right"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      <button class="carousel-control-prev" type="button" data-bs-target="#heroCarousel" data-bs-slide="prev">
        <span class="carousel-control-prev-icon"></span>
      </button>
      <button class="carousel-control-next" type="button" data-bs-target="#heroCarousel" data-bs-slide="next">
        <span class="carousel-control-next-icon"></span>
      </button>
    </div>

    <!-- ✨ Categories Section with Hover Effects -->
    <section v-if="categories.length > 0" class="categories-modern">
      <div class="container">
        <div class="section-header">
          <span class="section-subtitle">Discover</span>
          <h2 class="section-title-modern">Shop by Category</h2>
          <div class="title-underline"></div>
        </div>
        
        <div class="row g-4">
          <div
            class="col-6 col-md-4 col-lg-3"
            v-for="(cat, index) in categories"
            :key="cat._id"
            :data-aos="'fade-up'"
            :data-aos-delay="index * 100"
          >
            <a :href="`/shop?category=${cat._id}`" class="category-card-modern">
              <div class="category-image-wrapper">
                <img
                  :src="cat.categoryImageUrl"
                  :alt="cat.title"
                  class="category-image"
                />
                <div class="category-overlay">
                  <i class="fas fa-arrow-right"></i>
                </div>
              </div>
              <h5 class="category-title">{{ cat.title }}</h5>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- ✨ Products Section with Grid Animation -->
    <section v-if="products.length > 0" class="products-modern">
      <div class="container">
        <div class="section-header">
          <span class="section-subtitle">Featured</span>
          <h2 class="section-title-modern">Our Best Products</h2>
          <div class="title-underline"></div>
        </div>
        
        <div class="row g-4">
          <div
            class="col-6 col-md-4 col-lg-3"
            v-for="(product, index) in products"
            :key="product._id"
            :data-aos="'zoom-in'"
            :data-aos-delay="index * 50"
          >
            <div class="product-card-modern">
              <!-- Offer Badge -->
              <div v-if="product.offerPrice" class="offer-badge">
                <span>{{ product.offerPriceText || 'Sale' }}</span>
              </div>

              <!-- Product Image -->
              <a href="javascript:void(0)" @click="$router.push(`/product/${product.slug}`)" class="product-image-link">
                <div class="product-image-wrapper">
                  <img
                    :src="product.mainImageUrl"
                    :alt="product.name"
                    class="product-image"
                  />
                  <div class="product-overlay">
                    <button class="btn-quick-view">
                      <i class="fas fa-eye"></i>
                    </button>
                  </div>
                </div>
              </a>

              <!-- Product Details -->
              <div class="product-details">
                <h6 class="product-name">{{ product.name }}</h6>

                <!-- Price Display -->
                <div class="product-price">
                  <span v-if="product.offerPrice" class="price-original">₹{{ product.price }}</span>
                  <span class="price-current" :class="{ 'price-sale': product.offerPrice }">
                    ₹{{ product.offerPrice || product.price }}
                  </span>
                </div>

                <button @click="addProductToCart(product, true)" class="btn-add-cart">
                  <i class="fas fa-shopping-bag"></i>
                  <span>Buy Now</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ✨ Workshops Section with Card Animation -->
    <section v-if="workshops.length > 0" class="workshops-modern">
      <div class="container">
        <div class="section-header">
          <span class="section-subtitle">Learn & Create</span>
          <h2 class="section-title-modern">Upcoming Workshops</h2>
          <div class="title-underline"></div>
        </div>
        
        <div class="row g-4">
          <div
            class="col-md-6"
            v-for="(workshop, index) in workshops"
            :key="workshop._id"
            :data-aos="'fade-left'"
            :data-aos-delay="index * 150"
          >
            <div class="workshop-card-modern">
              <div class="workshop-icon">
                <i class="fas fa-palette"></i>
              </div>
              <div class="workshop-content">
                <h5 class="workshop-title">{{ workshop.title }}</h5>
                <div class="workshop-meta">
                  <div class="meta-item">
                    <i class="fas fa-calendar-alt"></i>
                    <span>{{ workshop.date }}</span>
                  </div>
                  <div class="meta-item">
                    <i class="fas fa-clock"></i>
                    <span>{{ workshop.time }}</span>
                  </div>
                  <div class="meta-item">
                    <i class="fas fa-map-marker-alt"></i>
                    <span>{{ workshop.address }}</span>
                  </div>
                </div>
                <div class="workshop-footer">
                  <span class="workshop-price">₹{{ workshop.price }}</span>
                  <a href="#" class="btn-register">
                    Register Now
                    <i class="fas fa-arrow-right"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ✨ Blog Section -->
    <section class="blog-modern">
      <div class="container">
        <div class="section-header">
          <span class="section-subtitle">Stories</span>
          <h2 class="section-title-modern">From Our Blog</h2>
          <div class="title-underline"></div>
        </div>
        
        <div class="row g-4">
          <div
            class="col-md-4"
            v-for="i in 3"
            :key="i"
            data-aos="flip-left"
            :data-aos-delay="i * 100"
          >
            <div class="blog-card-modern">
              <div class="blog-image-wrapper">
                <img src="images/blogs/blog1.jpg" alt="Blog" class="blog-image" />
                <div class="blog-date">
                  <span class="date-day">15</span>
                  <span class="date-month">Jan</span>
                </div>
              </div>
              <div class="blog-content">
                <h5 class="blog-title">How Handmade Gifts Create Memories</h5>
                <p class="blog-excerpt">Discover the joy and meaning behind handcrafted presents...</p>
                <a href="#" class="btn-read-more">
                  Read More
                  <i class="fas fa-arrow-right"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
        
        <div class="text-center mt-5">
          <a href="#" class="btn-view-all">
            View All Articles
            <i class="fas fa-arrow-right"></i>
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {useToast} from 'vue-toast-notification';
const $toast = useToast();
import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';
import BannerService from "../services/BannerService";
import ProductService from "../services/ProductService";
import { EventBus } from '@/eventBus'

export default {
  name: 'HomeView',
  components: {
    VueTelInput
  },
  data() {
    return {
      isDisabled: false,
      banners: [],
      categories: [],
      products: [],
      workshops: [],
    };
  },
  methods: {
    /**
     * Generate srcset for responsive images
     * Creates URLs with different widths for browser to choose from
     */
    generateSrcset(imageUrl) {
      if (!imageUrl) return '';
      
      const widths = [375, 550, 750, 1100, 1500, 1920];
      
      return widths
        .map(width => `${imageUrl}?width=${width} ${width}w`)
        .join(', ');
    },
    
    getBanners() {
      BannerService.getBanners()
        .then((response) => {
            this.banners = response.data.data;
        }).catch((e) => {
            this.$toast.error(e.response.data.respones_msg);
        });
    },
    
    getHomePageData() {
      ProductService.getHomePageData()
        .then((response) => {
            this.categories = response.data.data.categories;
            this.products = response.data.data.products;
            this.workshops = response.data.data.workshops;
        }).catch((e) => {
            this.$toast.error(e.response.data.respones_msg);
        });
    },
    
    async addProductToCart(product, redirectToCartPage = false) {
      let data = {
        productId: product._id,
        productDetail: product,
        qty: 1,
      };

      await ProductService.addProductToCart(data)
        .then((response) => {
          EventBus.emit('cart-updated')
          $toast.success(response.data.respones_msg);
          if (redirectToCartPage) {
            this.$router.push({ path: '/cart' });
          }
        })
        .catch((e) => {
          $toast.error(e.response.data.respones_msg);
        });
    },
  },
  mounted() {
    // Initialize AOS animation library
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-in-out',
        once: true,
        offset: 100
      });
    }
  },
  beforeMount() {
    this.getBanners()
    this.getHomePageData()
  },
}
</script>

<style scoped>
/* Import Modern Fonts */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Playfair+Display:wght@400;500;600;700&display=swap');

/* Global Styles */
.modern-home {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  overflow-x: hidden;
}

/* ========== HERO SECTION ========== */
.hero-slide-modern {
  height: 600px; /* Taller container */
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf0 100%);
  min-height: 400px;
  position: relative;
  overflow: hidden;
}

.hero-slide-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain; /* Changed from cover */
  object-position: center;
  z-index: 0;
}

.hero-overlay-modern {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.hero-content {
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 20px;
}

.animate-fade-in {
  animation: fadeInUp 1s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.hero-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  font-weight: 700;
  margin-bottom: 20px;
  line-height: 1.2;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.hero-description {
  font-size: clamp(1rem, 2vw, 1.3rem);
  margin-bottom: 35px;
  line-height: 1.6;
  font-weight: 300;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
}

.btn-hero-cta {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 16px 40px;
  background: rgb(118, 172, 175);
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 10px 30px rgba(118, 172, 175, 0.4);
}

.btn-hero-cta:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 40px rgba(118, 172, 175, 0.6);
  background: rgb(98, 152, 155);
  color: white;
}

.carousel-indicators button {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin: 0 6px;
  border: 2px solid white;
  opacity: 0.6;
  transition: all 0.3s;
}

.carousel-indicators button.active {
  opacity: 1;
  transform: scale(1.3);
}

/* ========== SECTION HEADERS ========== */
.categories-modern,
.products-modern,
.workshops-modern,
.blog-modern {
  padding: 100px 0;
  position: relative;
}

.categories-modern {
  background: #f8fafb;
}

.products-modern {
  background: white;
}

.workshops-modern {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.blog-modern {
  background: white;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-subtitle {
  color: rgb(118, 172, 175);
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  display: block;
  margin-bottom: 10px;
}

.section-title-modern {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 20px;
}

.title-underline {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, rgb(118, 172, 175), rgb(98, 152, 155));
  margin: 0 auto;
  border-radius: 2px;
}

/* ========== CATEGORY CARDS ========== */
.category-card-modern {
  display: block;
  text-decoration: none;
  color: inherit;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.category-card-modern:hover {
  transform: translateY(-10px);
}

.category-image-wrapper {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  margin-bottom: 15px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}

.category-image {
  width: 100%;
  height: 250px;
  object-fit: cover;
  transition: transform 0.6s ease;
}

.category-card-modern:hover .category-image {
  transform: scale(1.1);
}

.category-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(118,172,175,0.9), rgba(98,152,155,0.9));
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.4s;
}

.category-card-modern:hover .category-overlay {
  opacity: 1;
}

.category-overlay i {
  color: white;
  font-size: 2rem;
  animation: slideRight 0.6s ease;
}

@keyframes slideRight {
  from {
    transform: translateX(-20px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.category-title {
  font-weight: 600;
  font-size: 1.1rem;
  text-align: center;
  color: #2d3748;
}

/* ========== PRODUCT CARDS ========== */
.product-card-modern {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.product-card-modern:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 35px rgba(0,0,0,0.15);
}

.offer-badge {
  position: absolute;
  top: 15px;
  left: 15px;
  z-index: 10;
  background: linear-gradient(135deg, #f93a5a, #f7778c);
  color: white;
  padding: 8px 16px;
  border-radius: 30px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(249, 58, 90, 0.4);
}

.product-image-link {
  display: block;
  text-decoration: none;
}

.product-image-wrapper {
  position: relative;
  overflow: hidden;
  background: #f8f8f8;
}

.product-image {
  width: 100%;
  height: 280px;
  object-fit: cover;
  transition: transform 0.6s ease;
}

.product-card-modern:hover .product-image {
  transform: scale(1.15);
}

.product-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.4s;
}

.product-card-modern:hover .product-overlay {
  opacity: 1;
}

.btn-quick-view {
  background: white;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}

.btn-quick-view:hover {
  transform: scale(1.1);
  background: rgb(118, 172, 175);
  color: white;
}

.product-details {
  padding: 20px;
}

.product-name {
  font-weight: 600;
  font-size: 1rem;
  color: #2d3748;
  margin-bottom: 12px;
  line-height: 1.4;
  min-height: 40px;
}

.product-price {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.price-original {
  color: #a0aec0;
  text-decoration: line-through;
  font-size: 0.95rem;
}

.price-current {
  font-weight: 700;
  font-size: 1.3rem;
  color: #2d3748;
}

.price-current.price-sale {
  color: #f93a5a;
}

.btn-add-cart {
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-add-cart:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(118, 172, 175, 0.4);
}

/* ========== WORKSHOP CARDS ========== */
.workshop-card-modern {
  background: white;
  border-radius: 20px;
  padding: 30px;
  display: flex;
  gap: 25px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.workshop-card-modern:hover {
  transform: translateX(10px);
  box-shadow: 0 15px 50px rgba(0,0,0,0.2);
}

.workshop-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 8px 20px rgba(118, 172, 175, 0.3);
}

.workshop-icon i {
  color: white;
  font-size: 2rem;
}

.workshop-content {
  flex: 1;
}

.workshop-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.4rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 15px;
}

.workshop-meta {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #4a5568;
  font-size: 0.95rem;
}

.meta-item i {
  color: rgb(118, 172, 175);
  width: 18px;
}

.workshop-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid #e2e8f0;
}

.workshop-price {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
}

.btn-register {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  text-decoration: none;
  border-radius: 30px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s;
}

.btn-register:hover {
  transform: translateX(5px);
  box-shadow: 0 6px 20px rgba(118, 172, 175, 0.4);
  color: white;
}

/* ========== BLOG CARDS ========== */
.blog-card-modern {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.blog-card-modern:hover {
  transform: translateY(-10px) rotate(2deg);
  box-shadow: 0 15px 40px rgba(0,0,0,0.15);
}

.blog-image-wrapper {
  position: relative;
  overflow: hidden;
  height: 250px;
}

.blog-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s;
}

.blog-card-modern:hover .blog-image {
  transform: scale(1.2);
}

.blog-date {
  position: absolute;
  top: 20px;
  right: 20px;
  background: white;
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.date-day {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  line-height: 1;
}

.date-month {
  font-size: 0.75rem;
  color: rgb(118, 172, 175);
  text-transform: uppercase;
  font-weight: 600;
}

.blog-content {
  padding: 25px;
}

.blog-title {
  font-weight: 700;
  font-size: 1.2rem;
  color: #2d3748;
  margin-bottom: 12px;
  line-height: 1.4;
}

.blog-excerpt {
  color: #718096;
  font-size: 0.95rem;
  margin-bottom: 20px;
  line-height: 1.6;
}

.btn-read-more {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgb(118, 172, 175);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s;
}

.btn-read-more:hover {
  color: rgb(98, 152, 155);
  gap: 12px;
}

.btn-view-all {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 35px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.4s;
  box-shadow: 0 8px 20px rgba(118, 172, 175, 0.3);
}

.btn-view-all:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 30px rgba(118, 172, 175, 0.5);
  color: white;
}

/* ========== MOBILE RESPONSIVE ========== */
@media (max-width: 768px) {
  /* Hero - Shorter height shows more horizontal content */
  .hero-slide-modern {
    height: 400px; 
    min-height: 300px;
    max-height: 400px;
  }
  
  .hero-slide-img {
    object-fit: cover;
    object-position: center;
  }
  
  .hero-overlay-modern {
    background: linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.4) 100%);
  }
  
  .hero-content {
    padding: 0 15px;
    max-width: 90%;
  }
  
  .hero-title {
    font-size: clamp(1.5rem, 5vw, 2rem);
    margin-bottom: 10px;
  }
  
  .hero-description {
    font-size: clamp(0.85rem, 2.5vw, 1rem);
    margin-bottom: 15px;
  }
  
  .btn-hero-cta {
    padding: 10px 24px;
    font-size: 0.9rem;
  }
  
  /* Other sections */
  .categories-modern,
  .products-modern,
  .workshops-modern,
  .blog-modern {
    padding: 60px 0;
  }

  .section-header {
    margin-bottom: 40px;
  }

  .workshop-card-modern {
    flex-direction: column;
    text-align: center;
  }

  .workshop-footer {
    flex-direction: column;
    gap: 15px;
  }

  .category-image {
    height: 200px;
  }

  .product-image {
    height: 220px;
  }
}

@media (max-width: 480px) {
  .hero-slide-modern {
    height: 320px;
    min-height: 280px;
    max-height: 350px;
  }
  
  .hero-title {
    font-size: clamp(1.25rem, 6vw, 1.75rem);
  }
  
  .hero-description {
    font-size: 0.8rem;
  }
  
  .btn-hero-cta {
    padding: 8px 20px;
    font-size: 0.85rem;
  }
}

@media (max-width: 375px) {
  .hero-slide-modern {
    height: 300px;
    min-height: 280px;
  }
}
</style>